import styled from "styled-components";

import { fieldLabel } from "../../styles";

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: "Inter", sans-serif;
  color: var(--color-neutral-white);

  /* the two ways in, one under the other: the sign on the left, its title and hint, a "›" on the right */
  .choices {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .tile {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 14px;
  }

  .choice,
  .dropzone {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
    color: inherit;
    text-align: center;
    cursor: pointer;
    background: var(--color-neutral-base);
    border: 2px solid transparent;
    border-radius: 1rem;
    transition: border-color 0.15s;

    &:hover,
    &:focus-within,
    &.over {
      border-color: var(--color-brand-blue);
    }

    strong {
      font-size: 0.875rem;
    }

    span:not(.tile) {
      font-size: 0.75rem;
      opacity: 0.7;
    }
  }

  .choice {
    flex-direction: row;
    gap: 1rem;
    justify-content: flex-start;
    padding: 1.25rem;
    text-align: left;

    .text {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    /* as the option cards of Domínios */
    strong {
      font-size: 1.0625rem;
      font-weight: 800;
      line-height: 1.5rem;
    }

    /* as light as the modal's subtitle: readable on the grey card */
    .text span {
      font-size: 0.8125rem;
      line-height: 1.125rem;
      color: #a3a8ba;
      opacity: 1;
    }

    svg[role="img"] {
      flex-shrink: 0;
      border-radius: 14px;
    }

    .chevron {
      flex-shrink: 0;
      color: var(--color-neutral-150);
    }
  }

  .dropzone {
    min-height: 12rem;
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.25);

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
    }
  }

  .pexels {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    height: min(64vh, 560px);
  }

  /* the field and its "Buscar" */
  .search {
    display: flex;
    gap: 0.5rem;

    /* the field, with our own "x" to clear it on the right */
    .field {
      position: relative;
      display: flex;
      flex: 1;
      min-width: 0;
    }

    .clear {
      position: absolute;
      top: 50%;
      right: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      color: var(--color-neutral-white);
      background: rgba(255, 255, 255, 0.18);
      border-radius: 50%;
      transform: translateY(-50%);
      transition: background 0.15s;

      &:hover,
      &:focus-visible {
        background: rgba(255, 255, 255, 0.32);
      }
    }

    input {
      flex: 1;
      min-width: 0;
      height: 44px;
      padding: 0 2.75rem 0 1rem;
      color: inherit;
      background: var(--color-neutral-base);
      border: 2px solid transparent;
      border-radius: 12px;
      outline: none;

      &:focus {
        border-color: var(--color-brand-blue);
      }

      /* the browser's own "x" (blue, off our look) gives way to ours */
      &::-webkit-search-cancel-button,
      &::-webkit-search-decoration {
        display: none;
        appearance: none;
      }
    }

    > button {
      flex-shrink: 0;
      height: 44px;
      padding: 0 1.25rem;
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      background: var(--color-brand-blue);
      border-radius: 999px;
    }
  }

  .popular {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    align-items: center;
    font-size: 0.75rem;

    span {
      ${fieldLabel}
      margin-right: 0.25rem;
    }

    button {
      padding: 0.25rem 0.625rem;
      color: inherit;
      background: var(--color-neutral-base);
      border-radius: 999px;

      &:hover {
        background: rgba(255, 255, 255, 0.18);
      }
    }
  }

  /* only the results scroll: the search and the footer stay in sight */
  .results {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
  }

  /* 4 a row (3 on a phone): each cell about 162px wide, its thumbnail twice that */
  .grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem;

    @media (max-width: 640px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  /* the cover's: lying down (16:9), three a row */
  .landscape .grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .landscape .item {
    aspect-ratio: 16 / 9;
  }

  .item {
    position: relative;
    overflow: hidden;
    aspect-ratio: 3 / 4;
    border-radius: 10px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /* top right: the credit takes the bottom on hover */
    .duration {
      position: absolute;
      z-index: 1;
      top: 0.375rem;
      right: 0.375rem;
      padding: 0.125rem 0.375rem;
      font-size: 0.6875rem;
      font-weight: 700;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 6px;
    }

    /* the whole photo is the button: dimmed with "Adicionar" on hover (or when reached with Tab) */
    .pick {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      cursor: pointer;
      background: none;

      &::after {
        position: absolute;
        inset: 0;
        content: "";
        background: rgba(0, 0, 0, 0.45);
        opacity: 0;
        transition: opacity 0.15s;
      }
    }

    .cta {
      position: absolute;
      z-index: 1;
      padding: 0.5rem 1rem;
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      background: var(--color-brand-blue);
      border-radius: 999px;
      opacity: 0;
      transition: opacity 0.15s;
    }

    /* the photographer's credit: its own link, over the photo's bottom */
    .credit {
      position: absolute;
      right: 0.5rem;
      bottom: 0.5rem;
      left: 0.5rem;
      z-index: 2;
      overflow: hidden;
      font-size: 0.6875rem;
      /* tall enough for the descenders ("g", "p", "ç") */
      line-height: 1rem;
      color: var(--color-neutral-white);
      text-overflow: ellipsis;
      white-space: nowrap;
      opacity: 0;
      transition: opacity 0.15s;
    }

    &:hover,
    &:focus-within {
      .pick::after,
      .cta,
      .credit {
        opacity: 1;
      }
    }
  }

  /* the loader alone (nothing came yet): in the middle of the results */
  .loading.alone {
    flex-grow: 1;
    align-items: center;
  }

  /* "Carregar mais", at the end of the list, while there's more */
  .more {
    display: flex;
    justify-content: center;
    padding: 1rem 0 0.5rem;

    button {
      height: 40px;
      padding: 0 1.25rem;
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      background: var(--color-neutral-base);
      border-radius: 999px;

      &:hover {
        background: rgba(255, 255, 255, 0.18);
      }
    }
  }

  .loading,
  .empty {
    display: flex;
    justify-content: center;
    padding: 1.5rem 0;
    font-size: 0.8125rem;
    opacity: 0.8;
  }

  /* Pexels didn't answer */
  .error {
    font-size: 0.8125rem;
    color: var(--color-feedback-error, #ff6b6b);
  }
`;

/* Pexels' credit at the footer's left (the actions stay on the right): only its logo and text are the link */
export const Powered = styled.a`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  margin-right: auto;
  font-family: "Inter", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-neutral-white);
`;
